import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Fab,
  Typography,
  Divider,
} from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { stopDigitalDispatchTimer } from 'src/actions/digitalDispatchTimerActions';
import { makeStyles } from '@material-ui/styles';
import AvTimerIcon from '@material-ui/icons/AvTimer';
import { Link } from 'react-router-dom';
import useDigitalDispatchTimerDispatchUpdate from 'src/hooks/useDigitalDispatchTimerDispatchUpdate';

/**
 * @typedef {import('@material-ui/styles').ClassNameMap} ClassNameMap
 */

/**
 * @typedef {Object} ProviderAcceptPayload
 * @property {string} webPubSubUpdateType (only 'provideraccepted' events)
 * @property {string} providerId
 * @property {string} dispatchId
 * @property {number} eta
 * @property {number} estimatedPrice
 * @property {number} milesLoaded
 */

const DEFAULT_TIMEOUT_IN_SECONDS = 120;
const DISPATCH_LIVE_UPDATES_ROUTE_PREFIX = '/dispatch/dispatch/dispatch-schedule';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontFamily: theme.typography.fontFamily
  },
  extendedIcon: {
    marginRight: theme.spacing(1),
  },
  timerStickyButton: {
    position: 'sticky',
    zIndex: theme.zIndex.snackbar,
    left: '88%',
    top: '90%'
  },
  dialogContent: {
    padding: theme.spacing(3)
  },
  boldText: {
    fontWeight: theme.typography.fontWeightBold
  },
  dialogActionLink: {
    textDecoration: 'none',
    color: 'inherit'
  }
}));

/**
 * Renders when timer expires and no dispatch update
 * has come in for the allotted time period.
 * @param {number} dispatchId
 * @param {ClassNameMap} classes
 * @param {function} timerDialogLinkClicked
 * @returns {React.JSX.Element}
 */
function TimerExpiredDialogContent(dispatchId, classes, timerDialogLinkClicked) {
  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText>
          <Typography variant="body1">
            A provider has not been assigned after
            {' '}
            {DEFAULT_TIMEOUT_IN_SECONDS}
            {' '}
            seconds for Dispatch
            {' '}
            {dispatchId}
            ,
            please click the link below to submit more offers to providers
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={timerDialogLinkClicked}
        >
          <Link
            to={`${DISPATCH_LIVE_UPDATES_ROUTE_PREFIX}/${dispatchId}/edit`}
            className={classes.dialogActionLink}
          >
            Provider Submission Page
          </Link>
        </Button>
      </DialogActions>
    </>
  );
}

/**
 * Renders when timer didn't expire and a dispatch update
 * has come in for the allotted time period; meaning
 * a provider is now assigned for this particular dispatch
 * @param {number} dispatchId
 * @param {ClassNameMap} classes
 * @param {function} timerDialogLinkClicked
 * @param {ProviderAcceptPayload} providerAcceptanceStatus
 * @returns {React.JSX.Element}
 */
function TimerStoppedProviderAssignedDialogContent(
  dispatchId,
  classes,
  timerDialogLinkClicked,
  providerAcceptanceStatus
) {
  return (
    <>
      <DialogContent className={classes.dialogContent}>
        <DialogContentText>
          {providerAcceptanceStatus?.providerId ? (
            <Typography variant="body1">
              Provider with ID:
              {' '}
              {providerAcceptanceStatus.providerId}
              {' '}
              has accepted this dispatch with the following
              details:
            </Typography>
          ) : null}
          {providerAcceptanceStatus?.eta ? (
            <Typography variant="body1">
              <span className={classes.boldText}>
                ETA:
              </span>
              {' '}
              {`${providerAcceptanceStatus.eta} minutes`}
            </Typography>
          ) : null}
          {providerAcceptanceStatus?.estimatedPrice ? (
            <Typography variant="body1">
              <span className={classes.boldText}>
                Estimated Price:
              </span>
              {' '}
              {`$${providerAcceptanceStatus?.estimatedPrice?.toFixed(2)}`}
            </Typography>
          ) : null}
          {providerAcceptanceStatus?.milesLoaded ? (
            <Typography variant="body1">
              <span className={classes.boldText}>
                Miles Loaded:
              </span>
              {' '}
              {providerAcceptanceStatus.milesLoaded}
            </Typography>
          ) : null}
          <Typography variant="body1">
            Please click the link below to monitor the live updates for this
            dispatch
            {' '}
          </Typography>
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={timerDialogLinkClicked}
        >
          <Link
            to={`${DISPATCH_LIVE_UPDATES_ROUTE_PREFIX}/${dispatchId}/liveupdates`}
            className={classes.dialogActionLink}
          >
            Live Dispatch Updates Page
          </Link>
        </Button>
      </DialogActions>
    </>
  );
}

/**
 * Renders when timer has not yet expired and no
 * dispatch update has come in
 * @param {number} dispatchId
 * @param {ClassNameMap} classes
 * @param {number} timerSeconds
 * @returns {React.JSX.Element}
 */
function TimerRunningDialogContent(dispatchId, classes, timerSeconds) {
  return (
    <DialogContent className={classes.dialogContent}>
      <DialogContentText>
        <Typography variant="body1">
          Dispatch
          {' '}
          {dispatchId}
          {' '}
          is awaiting a provider to be assigned, if no
          provider accepts in the the next
          {' '}
          <span className={classes.boldText}>
            {timerSeconds}
          </span>
          {' '}
          seconds you will be prompted to submit more
          provider offers for this dispatch
        </Typography>
      </DialogContentText>
    </DialogContent>
  );
}

/**
 * Timer that has its state controlled through the redux store;
 * triggers whenever a batch of dispatch submission offers
 * is submitted to DigitalDispatch providers. The default timer is
 * 120 seconds; if a dispatch update for that particular dispatch
 * doesn't come back in that time-frame the dialog renders over
 * the screen and prompts the agent to submit more offers to
 * other providers.
 */
function DigitalDispatchTimer() {
  const classes = useStyles();
  const timerIntervalId = useRef(null);
  const {
    isTimerTriggered,
    dispatchId,
    timerDuration
  } = useSelector((state) => state.digitalDispatchTimer);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(timerDuration || DEFAULT_TIMEOUT_IN_SECONDS);
  const [isTimerExpired, setIsTimerExpired] = useState(false);
  const {
    isConnected,
    closeConnection,
    providerAcceptanceStatus,
    setProviderAcceptanceStatus
  } = useDigitalDispatchTimerDispatchUpdate(dispatchId);
  const dispatch = useDispatch();

  /**
   * Stops the setInterval controlling the timer
   * @returns {void}
   */
  const clearTimerInterval = () => {
    clearInterval(timerIntervalId.current);
  };

  /**
   * Dispatches the stop timer redux action
   * to set isTimerTriggered to false and
   * hide the dialog
   * @returns {void}
   */
  const timerDialogLinkClicked = () => {
    dispatch(stopDigitalDispatchTimer());
    setIsDialogOpen(false);
  };

  /**
   * Runs when the timer runs out of its allotted
   * time; displays the timeout dialog and renders
   * a link in the dialog to redirect user to provider
   * offer page
   * @returns {void}
   */
  const timerExpired = () => {
    clearTimerInterval();
    setIsTimerExpired(true);
    setIsDialogOpen(true);
  };

  /**
   * Reset the timer state to the default time
   * and sets state variables back to the initial
   * state
   * @returns {void}
   */
  const resetTimerState = () => {
    clearTimerInterval();
    setIsTimerExpired(false);
    setTimerSeconds(timerDuration || DEFAULT_TIMEOUT_IN_SECONDS);
    dispatch(stopDigitalDispatchTimer());
    setProviderAcceptanceStatus(null);
  };

  /**
   * Determines what content to render in the dialog based on
   * local state variables
   * @returns {React.JSX.Element}
   */
  const dialogContent = () => {
    if (isTimerExpired) {
      return TimerExpiredDialogContent(dispatchId, classes, timerDialogLinkClicked);
    } if (!isTimerExpired && providerAcceptanceStatus) {
      return TimerStoppedProviderAssignedDialogContent(
        dispatchId,
        classes,
        timerDialogLinkClicked,
        providerAcceptanceStatus
      );
    }
    return TimerRunningDialogContent(dispatchId, classes, timerSeconds,);
  };

  useEffect(() => {
    const startTimer = () => {
      timerIntervalId.current = setInterval(() => {
        setTimerSeconds((previousSeconds) => {
          if (timerIntervalId.current && previousSeconds === 0) {
            timerExpired();
            return previousSeconds;
          }
          return previousSeconds - 1;
        });
      }, 1000);
    };
    if (isTimerTriggered && timerDuration) {
      startTimer();
    } else if (timerDuration) {
      resetTimerState();
      if (isConnected) closeConnection();
    }
    return () => {
      clearTimerInterval();
      if (isConnected) closeConnection();
    };
  }, [isTimerTriggered, isConnected, timerDuration]);

  useEffect(() => {
    if (!isTimerExpired && providerAcceptanceStatus) {
      setIsDialogOpen(true);
    }
  }, [providerAcceptanceStatus]);

  /**
   * Sync timerSeconds with timerDuration when timer is triggered
   */
  useEffect(() => {
    if (isTimerTriggered && timerDuration) {
      setTimerSeconds(timerDuration);
    }
  }, [isTimerTriggered, timerDuration]);

  if (!isTimerTriggered) return null;
  return (
    isDialogOpen
      ? (
        <Dialog
          keepMounted
          open
          onClose={() => {
            if (!isTimerExpired) setIsDialogOpen(false);
          }}
          disableEscapeKeyDown
          className={classes.dialogRoot}
        >
          <DialogTitle
            disableTypography
            className={classes.dialogTitle}
            id="towmagic-dispatch-provider-offer-timer"
          >
            {`Dispatch ID: ${dispatchId} Provider Assignment Status`}
          </DialogTitle>
          <Divider />
          {dialogContent()}
        </Dialog>
      )
      : (
        <Fab
          className={classes.timerStickyButton}
          variant="extended"
          onClick={() => setIsDialogOpen(true)}
          color="primary"
        >
          <AvTimerIcon className={classes.extendedIcon} />
          {`${timerSeconds} | Dispatch ID: ${dispatchId} `}
        </Fab>
      )
  );
}

export default DigitalDispatchTimer;
